import React from 'react';
import LegalShell, { Section, P, UL, Notice } from '../components/LegalShell';

export default function Privacy() {
  return (
    <LegalShell
      eyebrow="Legal"
      title="Privacy Policy"
      intro="How MyDr.Ai collects, uses, stores, discloses, transfers and protects your personal data."
      version="1.0"
      lastUpdated="June 2026"
      governingLaw="Malaysia"
    >
      <Section id="introduction" number={1} heading="Introduction">
        <P>
          This Privacy Policy explains how <strong>Melonco Sdn. Bhd.</strong> (“MyDr.Ai”,
          “Company”, “we”, “our”, “us”) collects, uses, stores, discloses, transfers and
          protects your personal data when you access or use the MyDr.Ai platform, website,
          mobile applications, healthcare facilitation services, laboratory coordination
          services, healthcare record services, AI-powered services, and related offerings.
        </P>
        <P>This Privacy Policy is intended to comply with:</P>
        <UL>
          <li>Personal Data Protection Act 2010 (PDPA)</li>
          <li>Applicable Malaysian regulations</li>
          <li>Applicable healthcare confidentiality obligations</li>
          <li>Applicable cybersecurity obligations</li>
        </UL>
        <P>
          By accessing or using the Platform, you acknowledge that you have read and
          understood this Privacy Policy.
        </P>
      </Section>

      <Section id="data-controller" number={2} heading="Data controller">
        <P>The data controller responsible for your personal data is:</P>
        <UL>
          <li><strong>Melonco Sdn. Bhd.</strong> (Company Registration No. 1549767-D)</li>
          <li>Brand Name: MyDr.Ai</li>
          <li>
            Business Address: SERI GEMBIRA AVENUE NO. 6, JALAN SENANG RIA, TAMAN GEMBIRA,
            58200 KUALA LUMPUR, WILAYAH PERSEKUTUAN KUALA LUMPUR, MALAYSIA
          </li>
          <li>
            General Contact:{' '}
            <a href="mailto:hello@mydrai.co" className="text-brand-700 underline hover:no-underline">
              hello@mydrai.co
            </a>
          </li>
          <li>Telephone: +6011-2080 1876</li>
        </UL>
      </Section>

      <Section id="data-types" number={3} heading="Types of personal data we collect">
        <P>We may collect the following categories of personal data:</P>

        <P><strong>Identity Data</strong></P>
        <UL>
          <li>Full name</li>
          <li>NRIC number</li>
          <li>Passport number</li>
          <li>Nationality</li>
          <li>Gender</li>
          <li>Date of birth</li>
        </UL>

        <P><strong>Contact Data</strong></P>
        <UL>
          <li>Mobile number</li>
          <li>Email address</li>
          <li>Residential address</li>
          <li>Emergency contact details</li>
        </UL>

        <P><strong>Account Data</strong></P>
        <UL>
          <li>Username</li>
          <li>Password credentials</li>
          <li>Login history</li>
          <li>Authentication records</li>
        </UL>

        <P><strong>Transaction Data</strong></P>
        <UL>
          <li>Purchases</li>
          <li>Payment history</li>
          <li>Billing information</li>
          <li>Subscription records</li>
        </UL>

        <P><strong>Technical Data</strong></P>
        <UL>
          <li>IP address</li>
          <li>Browser type</li>
          <li>Device information</li>
          <li>Operating system</li>
          <li>Platform usage logs</li>
          <li>Security logs</li>
        </UL>

        <P><strong>Healthcare Data (Sensitive Personal Data)</strong></P>
        <UL>
          <li>Medical history</li>
          <li>Symptoms</li>
          <li>Allergies</li>
          <li>Current medications</li>
          <li>Laboratory reports</li>
          <li>Healthcare records</li>
          <li>Healthcare provider notes</li>
          <li>Uploaded health documents</li>
        </UL>
        <Notice tone="info">
          Health information is treated as sensitive personal data under the
          PDPA. Before any healthcare data is collected, you must accept our{' '}
          <a href="/sensitive-data-consent" className="text-brand-700 underline hover:no-underline">
            Sensitive Personal Data Consent
          </a>
          , which explains exactly what we collect, why, who can see it and how
          you can withdraw consent.
        </Notice>
      </Section>

      <Section id="sources" number={4} heading="Sources of data">
        <P>We may collect data:</P>
        <UL>
          <li>Directly from you</li>
          <li>Through onboarding forms</li>
          <li>Through healthcare questionnaires</li>
          <li>Through healthcare providers</li>
          <li>Through pharmacies</li>
          <li>Through laboratories</li>
          <li>Through uploaded documents</li>
          <li>Through customer support interactions</li>
          <li>Through platform usage</li>
        </UL>
      </Section>

      <Section id="purposes" number={5} heading="Purposes of processing">
        <P>We may process your personal data for the following purposes:</P>

        <P><strong>Service delivery</strong></P>
        <UL>
          <li>Account creation</li>
          <li>Identity verification</li>
          <li>Customer support</li>
          <li>Service administration</li>
        </UL>

        <P><strong>Healthcare facilitation</strong></P>
        <UL>
          <li>Appointment coordination</li>
          <li>Healthcare provider matching</li>
          <li>Prescription facilitation</li>
          <li>Laboratory coordination</li>
        </UL>

        <P><strong>Health record management</strong></P>
        <UL>
          <li>Storage of health records</li>
          <li>Aggregation of laboratory data</li>
          <li>Medical document management</li>
        </UL>

        <P><strong>Future health services</strong></P>
        <UL>
          <li>AI health insights</li>
          <li>Preventive health programs</li>
          <li>Wellness programs</li>
          <li>Longevity programs</li>
        </UL>

        <P><strong>Compliance</strong></P>
        <UL>
          <li>Regulatory obligations</li>
          <li>Legal obligations</li>
          <li>Fraud prevention</li>
          <li>Security monitoring</li>
        </UL>

        <P><strong>Communications</strong></P>
        <UL>
          <li>Service updates</li>
          <li>Security notifications</li>
          <li>Transaction confirmations</li>
          <li>Customer support responses</li>
        </UL>
      </Section>

      <Section id="disclosure" number={6} heading="Disclosure of personal data">
        <P>We may disclose personal data to:</P>

        <P><strong>Healthcare providers</strong> — doctors, specialists, clinics, hospitals —
        for healthcare facilitation purposes.</P>

        <P><strong>Pharmacies</strong> — for prescription fulfilment purposes.</P>

        <P><strong>Laboratories</strong> — for laboratory testing coordination purposes.</P>

        <P><strong>Service providers</strong>, including cloud providers, payment processors,
        customer support providers, and security providers.</P>

        <P><strong>Government authorities</strong> where required by law.</P>

        <P><strong>Professional advisers</strong>, including lawyers, auditors, accountants and
        compliance consultants where reasonably necessary.</P>
      </Section>

      <Section id="international" number={7} heading="International data transfers">
        <P>
          MyDr.Ai currently intends to host primary healthcare data within Malaysia. However,
          certain operational service providers may process limited information outside Malaysia
          where legally permitted. Where cross-border transfers occur, MyDr.Ai shall take
          reasonable measures to ensure appropriate safeguards are implemented.
        </P>
      </Section>

      <Section id="security" number={8} heading="Data security">
        <P>MyDr.Ai implements reasonable technical and organisational measures to protect
        personal data, including:</P>
        <UL>
          <li>Encryption</li>
          <li>Access controls</li>
          <li>Authentication systems</li>
          <li>Security monitoring</li>
          <li>Audit logging</li>
          <li>Cybersecurity controls</li>
        </UL>
        <Notice tone="warning">
          No system can be guaranteed completely secure. Users acknowledge that transmission of
          information over the internet carries inherent risks.
        </Notice>
      </Section>

      <Section id="retention" number={9} heading="Data retention">
        <P>We retain personal data only for as long as reasonably necessary for:</P>
        <UL>
          <li>Service delivery</li>
          <li>Regulatory compliance</li>
          <li>Healthcare record management</li>
          <li>Legal obligations</li>
          <li>Fraud prevention</li>
          <li>Dispute resolution</li>
        </UL>
        <P>Certain healthcare-related information may be retained for extended periods where
        required by law or industry practice.</P>
      </Section>

      <Section id="rights" number={10} heading="Your rights">
        <P>Subject to applicable law, you may have rights to:</P>
        <UL>
          <li>Access personal data</li>
          <li>Correct inaccurate data</li>
          <li>Withdraw consent</li>
          <li>Request deletion</li>
          <li>Restrict processing</li>
          <li>Object to certain processing activities</li>
        </UL>
        <P>
          Requests may be submitted to{' '}
          <a href="mailto:hello@mydrai.co" className="text-brand-700 underline hover:no-underline">
            hello@mydrai.co
          </a>
          . MyDr.Ai may require identity verification before processing requests.
        </P>
      </Section>

      <Section id="cookies" number={11} heading="Cookies and tracking technologies">
        <P>The Platform may use:</P>
        <UL>
          <li>Cookies</li>
          <li>Analytics technologies</li>
          <li>Security technologies</li>
          <li>Session management technologies</li>
        </UL>
        <P>to improve user experience and platform security. Users may adjust browser settings
        to manage cookies.</P>
      </Section>

      <Section id="children" number={12} heading="Children">
        <P>The Platform is generally intended for individuals aged eighteen (18) years and
        above. Where permitted, information relating to minors may be processed through parents,
        guardians or legally authorised representatives.</P>
      </Section>

      <Section id="changes" number={13} heading="Changes to this policy">
        <P>MyDr.Ai may amend this Privacy Policy from time to time. Updated versions shall be
        published on the Platform. Continued use of the Platform constitutes acceptance of the
        updated Privacy Policy.</P>
      </Section>

      <Section id="contact" number={14} heading="Contact">
        <P>
          Privacy enquiries may be directed to{' '}
          <a href="mailto:hello@mydrai.co" className="text-brand-700 underline hover:no-underline">
            hello@mydrai.co
          </a>
          .
        </P>
        <P><strong>Melonco Sdn. Bhd.</strong> (Company Registration No. 1549767-D)</P>
        <P>MyDr.Ai</P>
        <P>SERI GEMBIRA AVENUE NO. 6, JALAN SENANG RIA, TAMAN GEMBIRA, 58200 KUALA LUMPUR,
        WILAYAH PERSEKUTUAN KUALA LUMPUR, MALAYSIA</P>
      </Section>
    </LegalShell>
  );
}
